import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Chart from './Chart';
import Category from '../../models/Category.jsx';
import Colors from '../../utilities/Color';
import moneyFormat, { moneyFormatFromCents } from '../../utilities/moneyFormat';
import getCategoryGroupsByType from '../../utilities/getCategoryGroupsByType';
import Tabs from './Tabs';
import DateHelper from '../../utilities/DateHelper';
import MoneyComponent from '../elements/MoneyComponent';
import { netSpendingTabChanged } from '../../redux/actions/url.js';
import Tab from './navigation/Tab.jsx';

function NetIncome({ categories, categoryGroups, selectedDates, visibleDates }) {

	const dispatch = useDispatch();
	const netSpendingTab = useSelector((state) => state.url.netSpendingTab);

	const graphTooltipFormat = useCallback((tooltipData, d) => {
		const dataSetName = d.datasets[tooltipData.datasetIndex].label;
		return `${dataSetName}: ${moneyFormatFromCents(tooltipData.yLabel)}`;
	}, []);

	const tabChange = (tab) => {
		dispatch(netSpendingTabChanged(tab));
	};

	function renderTableFromGraphData({ graphData, xAxisName, extraColumns = [] }) {
		return (
			<table className='tabel'>
				<thead>
				<tr>
					<th>{xAxisName}</th>
					{graphData.datasets.map((dataset) => (
						<th key={dataset.label}>{dataset.label}</th>
					))}
					{extraColumns.map((column) => (
						<th
							key={column.label}
						>
							{column.label}
						</th>
					))}
				</tr>
				</thead>
				<tbody>
				{graphData.labels.map((label, index) => {
					return (<tr
							key={label}
						>
							<td>{label}</td>
							{graphData.datasets.map((dataset, datasetIndex) => {
								return (
									<td key={dataset.label}>
										<MoneyComponent
											flipPositivity={dataset.displayNegative}
											amount={dataset.data[index]}
										/>
									</td>
								);
							})}
							{extraColumns.map((column) => (
								<td
									key={column.label}
								>
									{column.calculateValueFromDataSets(graphData.datasets, index)}
								</td>
							))}
						</tr>
					);
				})}
				</tbody>
			</table>
		);
	}

	function getGraphDataByMonth(datesSorted, byDayInstead) {
		const infoByDate = new Map();

		const spendingByMonthData = {
			labels: [],
			datasets: []
		};

		const incomeDataset = {
			label: 'Income',
			backgroundColor: Colors.getDarkColorRgba(1, .5),
			borderColor: Colors.getDarkColorRgba(1, 1),
			data: [],
		};
		const expenseDataset = {
			label: 'Spent',
			backgroundColor: Colors.getDarkColorRgba(3, .5),
			borderColor: Colors.getDarkColorRgba(3, 1),
			displayNegative: true,
			data: [],
		};
		const totalSpentDataset = {
			label: 'Total Spent',
			backgroundColor: Colors.getDarkColorRgba(3, .5),
			borderColor: Colors.getDarkColorRgba(3, 1),
			data: [],
		};
		const netDataset = {
			label: 'Net',
			backgroundColor: Colors.getDarkColorRgba(2, .5),
			borderColor: Colors.getDarkColorRgba(2, 1),
			data: [],
		};

		let dateFormat = 'MMMM YYYY';
		if(byDayInstead) {
			dateFormat = 'MMMM DD YYYY';
		}

		let totalSpentRunningTotal = 0;
		let totalBudgeted = null;
		let totalMadeRunningTotal = 0;
		console.time('summaryTotals');
		for(let date of datesSorted) {
			const dateKey = date.format(dateFormat);
			if(!infoByDate.has(dateKey)) {
				infoByDate.set(dateKey, {
					spent: 0,
					income: 0,
				});
			}
			let currentInfo = infoByDate.get(dateKey);
			const {
				totalSpent,
				totalMade,
				totalSaved,
				totalSpentOnLoans
			} = Category.getSummaryTotals(categories, categoryGroups, date, byDayInstead ? date : undefined, byDayInstead ? 'day' : undefined);

			currentInfo.income = totalMade;
			currentInfo.spent = totalSpent;


			spendingByMonthData.labels.push(dateKey);
			incomeDataset.data.push(currentInfo.income);
			expenseDataset.data.push(currentInfo.spent);

			totalMadeRunningTotal += currentInfo.income;
			totalSpentRunningTotal += currentInfo.spent;
			totalSpentDataset.data.push(totalSpentRunningTotal);
			netDataset.data.push(currentInfo.income - currentInfo.spent);
		}
		console.timeEnd('summaryTotals');

		console.time('byDaySomething?');
		if(datesSorted.length && byDayInstead && datesSorted.length < 40) { // 40 seems like a good number
			const result = Category.getSummaryTotals(categories, categoryGroups, datesSorted[0]);
			totalBudgeted = result.totalBudgeted;
		}
		console.timeEnd('byDaySomething?');


		const netGraphData = _.cloneDeep(spendingByMonthData);
		const totalSpentData = _.cloneDeep(spendingByMonthData);

		spendingByMonthData.datasets.push(incomeDataset);
		spendingByMonthData.datasets.push(expenseDataset);

		totalSpentData.datasets.push(totalSpentDataset);

		netGraphData.datasets.push(netDataset);


		return {
			totalBudgetedDataDays: totalBudgeted,
			totalSpentData,
			spendingByMonthData,
			netGraphData,
			totalSpentRunningTotal,
			totalMadeRunningTotal
		};
	}

	const datesByDay = new Map();

	let lowestDateWithActivity = null;
	let highestDateWithActivity = null;

	console.time('getAllTransactions');
	const transactions = transactionFinder.getAllTransactions();
	console.timeEnd('getAllTransactions');

	console.time('getHighestAndLowestTransactionDates');
	for(const transaction of transactions) {
		if(!transaction.transaction_date) // Why are there null transactions...
		{
			continue;
		}
		if(transaction.transaction_date.isBefore(lowestDateWithActivity) || !lowestDateWithActivity) {
			lowestDateWithActivity = transaction.transaction_date;
		}
		if(transaction.transaction_date.isAfter(highestDateWithActivity) || !highestDateWithActivity) {
			highestDateWithActivity = transaction.transaction_date;
		}
	}
	console.timeEnd('getHighestAndLowestTransactionDates');

	console.time('determiningVisibleDatesToShow');
	const datesSorted = [];
	for(const visibleDate of visibleDates) {
		if(visibleDate.isSameOrAfter(lowestDateWithActivity, 'month') && visibleDate.isSameOrBefore(highestDateWithActivity, 'month')) {
			datesSorted.push(visibleDate);
		}
	}
	console.timeEnd('determiningVisibleDatesToShow');

	console.time('dates');
	_.each(selectedDates, (month) => {
		const theDays = DateHelper.getDaysForMonth(month);
		for(let day of theDays) {
			datesByDay.set(day.format('MMMM YYYY DD'), day);
		}
	});
	console.timeEnd('dates');

	console.time('graphDataByMonth');
	const { netGraphData, spendingByMonthData } = getGraphDataByMonth(datesSorted);
	console.timeEnd('graphDataByMonth');

	console.time('graphDataByDay');
	if(netSpendingTab !== 'spending-by-day')
	{
		datesByDay.clear();
	}

	const {
		totalBudgetedDataDays, totalSpentData,
		totalSpentRunningTotal,
		spendingByMonthData: spendingByMonthDataDays,
		netGraphData: netGraphDataDays,
		totalMadeRunningTotal
	} = getGraphDataByMonth(Array.from(datesByDay).map(([id, date]) => {
		return date;
	}), true);
	console.timeEnd('graphDataByDay');

	const elements = {
		line: {
			tension: 0,
		},
		rectangle: {
			borderWidth: 2,
		}
	};
	const tooltips = {
		intersect: false,
		// mode: 'index',
		callbacks: {
			label: graphTooltipFormat
		},
	};
	const scales = {
		yAxes: [{
			ticks: {
				beginAtZero: true,
				callback: Chart.yAxesMoneyFormat
			}
		}]
	};

	const spendingByDayOptions = {
		elements: elements,
		responsive: true,
		title: {
			display: false,
		},
		tooltips,
		scales,
		annotation: {}
	};

	if(totalBudgetedDataDays) {
		spendingByDayOptions.scales.yAxes[0].ticks.suggestedMax = totalBudgetedDataDays;
		spendingByDayOptions.annotation.annotations = [
			{
				type: 'line',
				mode: 'horizontal',
				scaleID: 'y-axis-0',
				value: totalBudgetedDataDays,
				borderColor: 'red',
				label: {
					content: `Total Budgeted ${moneyFormatFromCents(totalBudgetedDataDays)}`,
					enabled: true,
					position: 'top'
				}
			}
		];
	}

	const netTable = (
		<div className='row'>
			<div className='col s12'>
				{renderTableFromGraphData({
					graphData: spendingByMonthData, xAxisName: 'Months', extraColumns: [
						{
							label: 'Result',
							calculateValueFromDataSets: (datasets, index) => {
								return <MoneyComponent
									amount={datasets[0].data[index] - datasets[1].data[index]}
								/>;
							}
						}
					]
				})}
			</div>
		</div>
	);

	return (
		<Tabs
			activeTab={netSpendingTab}
			tabsClasses='inline'
			onTabChange={tabChange}
		>
			<Tab label='Spending By Month'>
				<Chart
					type='line'
					data={spendingByMonthData}
					options={{
						elements: elements,
						responsive: true,
						title: {
							display: false,
						},
						tooltips,
						scales,
					}}
				/>
				{netTable}
			</Tab>
			<Tab label='Net'>
				<Chart
					type='bar'
					data={netGraphData}
					options={{
						elements,
						responsive: true,
						title: {
							display: false,
						},
						tooltips,
						scales,
					}}
				/>
				{netTable}
			</Tab>
			<Tab label='Spending By Day'>
				<Chart
					type='line'
					data={totalSpentData}
					options={spendingByDayOptions}
				/>
				<div>
					{renderTableFromGraphData({
						graphData: totalSpentData,
						xAxisName: 'Day'
					})
					}
				</div>
			</Tab>
		</Tabs>
	);
}

function mapStateToProps(state) {
	return {
		...state.dashboard,
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({}, dispatch);
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(NetIncome);